import { forwardRef, ReactNode, useEffect, useMemo, useState } from 'react';
import { DayPicker, DayPickerProps } from 'react-day-picker';
import {
  addDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { Button, ButtonProps } from '../Button';
import { Icon } from '../Icon';
import { useControlledState } from '../../hooks/useControlledState';
import {
  Popover,
  PopoverContent,
  PopoverContentProps,
  PopoverTrigger,
} from '../Popover';
import { ListBox, Option } from '../ListBox';
import { Box, BoxProps } from '../Box';
import { Text } from '../Text';
import { Divider } from '../Divider';

export interface DateRangePickerProps
  extends Omit<ButtonProps, 'value' | 'defaultValue' | 'onChange'> {
  as?: React.ElementType;
  value?: [Date, Date];
  defaultValue?: [Date, Date];
  onChange?: (date?: [Date, Date]) => void;
  captionLayout?: DayPickerProps['captionLayout'];
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  format?: string;
  error?: string;
  readOnly?: boolean;
  placeholder?: string;
  defaultMonths?: [Date, Date];
  align?: PopoverContentProps['align'];
  maxRange?: number;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  clearable?: boolean;
  onClear?: () => void;
}

export interface PredefinedRangeProps {
  id: string;
  label: string;
  value: [Date, Date];
  css?: BoxProps['css'];
}

const sortDates = (dateRange: [Date, Date]): [Date, Date] => {
  const [date1, date2] = dateRange;
  if (date1 <= date2) {
    return [date1, date2];
  } else {
    return [date2, date1];
  }
};

const DateRangePickerLayout = ({ css, ...props }: BoxProps) => {
  return (
    <Box
      css={{
        display: 'grid',
        gridTemplateAreas: `
          "list calendar calendar"
          "list footer footer"
        `,
        gridTemplateRows: '1fr auto',

        ...css,
      }}
      {...props}
    />
  );
};

DateRangePickerLayout.List = function List({ css, ...props }: BoxProps) {
  return <Box css={{ gridArea: 'list', ...css }} {...props} />;
};

DateRangePickerLayout.Calendar = function Calendar({
  css,
  ...props
}: BoxProps) {
  return <Box css={{ gridArea: 'calendar', ...css }} {...props} />;
};

DateRangePickerLayout.Footer = function Footer({ css, ...props }: BoxProps) {
  return <Box css={{ gridArea: 'footer', ...css }} {...props} />;
};

export const DateRangePicker = forwardRef<
  HTMLButtonElement,
  DateRangePickerProps
>(
  (
    {
      value,
      defaultValue,
      onChange,
      format: dateFormat = 'PP',
      leftIcon,
      rightIcon,
      css,
      captionLayout = 'dropdown',
      placeholder = 'Select Date',
      align = 'end',
      defaultMonths,
      error,
      readOnly,
      maxRange = 365,
      open: controlledOpen,
      defaultOpen,
      onOpenChange,
      clearable,
      onClear,
      ...rest
    },
    ref,
  ) => {
    const handleChange = (date: [Date, Date] | undefined) => {
      if (date) {
        date = sortDates(date);
      }
      onChange?.(date);
    };

    const [dateRange, setDateRange] = useControlledState<
      [Date, Date] | undefined
    >(value, defaultValue, handleChange);

    useEffect(() => {
      if (dateRange && !value) {
        setDateRange(undefined);
      }
    }, [value]);

    rightIcon =
      !clearable || !dateRange ? (
        <Icon inherit name='calendar' size={16} />
      ) : (
        <Icon
          name='x'
          color='$red-500'
          size={20}
          onClick={() => {
            onClear?.();
            setOpen(false);
          }}
        />
      );

    const [open, setOpen] = useControlledState<boolean>(
      controlledOpen,
      defaultOpen,
      onOpenChange,
    );

    const [displayMonths, setDisplayMonths] = useState<[Date, Date]>(
      dateRange ?? defaultMonths ?? [new Date(), new Date()],
    );

    // if the date range value changes, the month being displayed should change
    useEffect(() => {
      if (!dateRange) return;

      const [start, end] = dateRange;
      const [displayStart, displayEnd] = displayMonths;

      if (format(start, 'MMMM yyyy') !== format(displayStart, 'MMMM yyyy')) {
        setDisplayMonths((prevMonths) => [start, prevMonths[1]]);
      }

      if (format(end, 'MMMM yyyy') !== format(displayEnd, 'MMMM yyyy')) {
        setDisplayMonths((prevMonths) => [prevMonths[0], end]);
      }
    }, [dateRange]);

    const startDate = dateRange?.[0];
    const endDate = dateRange?.[1];

    const predefinedRanges: PredefinedRangeProps[] = [
      {
        id: 'last-30-days',
        label: 'Last 30 days',
        value: [subDays(new Date(), 30), endOfDay(new Date())],
      },
      {
        id: 'last-14-days',
        label: 'Last 14 days',
        value: [subDays(new Date(), 14), endOfDay(new Date())],
      },
      {
        id: 'last-7-days',
        label: 'Last 7 days',
        css: {
          marginBottom: 16,
        },
        value: [subDays(new Date(), 7), endOfDay(new Date())],
      },
      {
        id: 'today',
        label: 'Today',
        value: [startOfDay(new Date()), endOfDay(new Date())],
      },
      {
        id: 'yesterday',
        label: 'Yesterday',
        value: [
          startOfDay(subDays(new Date(), 1)),
          endOfDay(subDays(new Date(), 1)),
        ],
      },
      {
        id: 'this-week',
        label: 'This week',
        value: [startOfWeek(new Date()), endOfWeek(new Date())],
      },
      {
        id: 'last-week',
        label: 'Last week',
        value: [
          startOfWeek(subWeeks(new Date(), 1)),
          endOfWeek(subWeeks(new Date(), 1)),
        ],
      },
      {
        id: 'this-month',
        label: 'This month',
        value: [startOfMonth(new Date()), endOfMonth(new Date())],
      },
      {
        id: 'last-month',
        label: 'Last month',
        value: [
          startOfMonth(subMonths(new Date(), 1)),
          endOfMonth(subMonths(new Date(), 1)),
        ],
      },
    ];

    const handleChangePredefinedRange = (ids: (string | number)[]) => {
      const range = predefinedRanges.find((r) => r.id === ids[0]);
      if (range) {
        setDateRange(range.value);
        setDisplayMonths(range.value);
      }
    };

    const disabledDates = useMemo(() => {
      if (!startDate || !endDate) {
        return undefined;
      }
      const sortedDates = sortDates([startDate, endDate]);

      return {
        before: subDays(sortedDates[1], maxRange),
        after: addDays(sortedDates[0], maxRange),
      };
    }, [startDate, endDate, maxRange]);

    const selectedPredefinedRange = predefinedRanges.find((range) => {
      const formattedRangeStart = format(range.value[0], 'yyyy-MM-dd');
      const formattedRangeEnd = format(range.value[1], 'yyyy-MM-dd');
      const formattedStartDate = format(startDate ?? new Date(), 'yyyy-MM-dd');
      const formattedEndDate = format(endDate ?? new Date(), 'yyyy-MM-dd');

      return (
        formattedRangeStart === formattedStartDate &&
        formattedRangeEnd === formattedEndDate
      );
    });

    const formattedValue = (dateRange ?? [])
      .map((date) => format(date, dateFormat))
      .join(' — ');

    return (
      <Popover
        open={open}
        onOpenChange={(newOpen) => {
          if (dateRange) {
            setDisplayMonths(dateRange);
          }
          setOpen(newOpen);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            data-has-value={!!dateRange}
            variant='input'
            data-invalid={!!error}
            aria-invalid={!!error}
            data-readonly={readOnly}
            css={{
              position: 'relative',
              ...css,
            }}
            {...rest}
          >
            <span style={{ flexShrink: 0 }}>{leftIcon}</span>
            <span style={{ flex: 1 }}>{formattedValue || placeholder}</span>
            <span style={{ flexShrink: 0 }}>{rightIcon}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          arrow={false}
          align={align}
          css={{ p: 0, overflow: 'hidden' }}
        >
          <DateRangePickerLayout>
            <DateRangePickerLayout.List>
              <ListBox
                value={[selectedPredefinedRange?.id]}
                onChange={handleChangePredefinedRange}
                options={predefinedRanges}
                css={{
                  width: 160,
                  py: 20,
                  px: 8,
                  height: '100%',
                  borderRight: '1px solid $border-light',
                  overflow: 'auto',
                }}
              >
                {(option: PredefinedRangeProps) => (
                  <Option css={option.css}>
                    <Text size='xs'>{option.label}</Text>
                  </Option>
                )}
              </ListBox>
            </DateRangePickerLayout.List>
            <DateRangePickerLayout.Calendar css={{ display: 'flex' }}>
              <Box
                css={{
                  p: 16,
                }}
              >
                <DayPicker
                  mode='single'
                  captionLayout={captionLayout}
                  styles={{
                    root: {
                      fontFamily: 'Inter, sans-serif',
                    },
                  }}
                  autoFocus
                  month={displayMonths?.[0]}
                  onMonthChange={(date: Date) =>
                    setDisplayMonths([date, displayMonths[1]])
                  }
                  required={true}
                  selected={startDate}
                  disabled={disabledDates}
                  onSelect={(date: Date) =>
                    setDateRange([date, endDate ?? new Date()])
                  }
                />
              </Box>
              <Divider
                orientation='vertical'
                css={{
                  backgroundColor: '$border-light',
                }}
              />
              <Box
                css={{
                  p: 16,
                }}
              >
                <DayPicker
                  mode='single'
                  captionLayout={captionLayout}
                  styles={{
                    root: {
                      fontFamily: 'Inter, sans-serif',
                    },
                  }}
                  month={displayMonths?.[1]}
                  onMonthChange={(date: Date) =>
                    setDisplayMonths([displayMonths[0], date])
                  }
                  required={true}
                  selected={endDate}
                  onSelect={(date: Date) =>
                    setDateRange([startDate ?? new Date(), date])
                  }
                  disabled={disabledDates}
                />
              </Box>
            </DateRangePickerLayout.Calendar>
            <DateRangePickerLayout.Footer
              css={{
                display: 'flex',
                justifyContent: 'end',
                alignItems: 'center',
                borderTop: '1px solid $border-light',
                px: 16,
                py: 8,
              }}
            >
              <Button onClick={() => setOpen(false)}>Done</Button>
            </DateRangePickerLayout.Footer>
          </DateRangePickerLayout>
        </PopoverContent>
      </Popover>
    );
  },
);

DateRangePicker.displayName = 'DateRangePicker';
